import {
  Component,
  ElementRef,
  Input,
  inject,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges
} from '@angular/core'
import { CatalogueEntry, CatalogueService, Field, StoredEntityService } from '@dot-it/jml-lib'
import { Observable, of } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { EntityReference } from '../../../../models/entity-reference'

@Component({
  selector: 'jml-lib-drop-down-wrapper',
  templateUrl: './drop-down-wrapper.component.html',
  styleUrls: ['./drop-down-wrapper.component.scss']
})
export class DropDownWrapperComponent implements OnInit, OnChanges {
  /**
   * WrapperComponent for drop down cell.
   *
   * It covers 3 scenarios.
   *  1) Dynamic drop downs:
   *     Dyamic drop down are defined in the dto config by setting the param "catalogueExpression".
   *     If catalogueExpression exists, for each cell the dynamic options are retrieved from the backend.
   *     Dynamic drop downs are refreshed with each entity update.
   *  2) Static drop down:
   *     Static dropdowns may be passed in via @Input() staticOptions. If they exist they will be taken.
   *  3) Static drop down but options not passed in:
   *     In case none of the above it the case static options are retrieved from the backend. Used for FieldsViewComponent.
   *
   * After evaluation the actual drop down cell component is called.
   *
   */
  @Input() staticOptions?: CatalogueEntry[]
  @Input() tableReference: ElementRef
  @Input() editable = false
  @Input() rowData: any
  @Input() field: Field
  @Input() dtoType: string
  @Input() entityId: number
  @Input() customEdit = false

  @Output() completeCellEdit: EventEmitter<any> = new EventEmitter()
  @Output() onCustomEdit: EventEmitter<any> = new EventEmitter()

  options$: Observable<CatalogueEntry[]>

  selectedCatalogueEntry: CatalogueEntry | undefined

  private catalogueService = inject(CatalogueService)
  private storedEntityService = inject(StoredEntityService)

  ngOnInit(): void {
    if (this.field?.catalogueExpression) {
      this.options$ = this.loadCatalogueByExpression()
    } else if (this?.staticOptions) {
      this.options$ = of(this.staticOptions)
    } else {
      this.options$ = this.loadCatalogue()
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.rowData) {
      this.setSelectedValue(this.rowData[this.field.field])
    }
  }

  setSelectedValue(catalogueEntry: CatalogueEntry) {
    this.selectedCatalogueEntry = catalogueEntry
  }

  private loadCatalogueByExpression(): Observable<CatalogueEntry[]> {
    const reference = new EntityReference(this.dtoType, this.entityId)
    // Observable returned by storedEntityService fires each time when the entity is updated.
    // This ensures that the dynamic options are reloaded when an entity is updated.
    return this.storedEntityService.getEntity$(this.dtoType, this.entityId).pipe(
      switchMap((_) => this.catalogueService.getCatalogueByExpression(this.field.catalogueExpression, reference)),
      map((catalogue) => (!catalogue?.values ? [] : catalogue?.values))
    )
  }

  private loadCatalogue(): Observable<CatalogueEntry[]> {
    return this.catalogueService.getCatalogue(this.field.defaultCatalogue).pipe(map((catalogue) => catalogue.values))
  }

  emitComplete(event: any) {
    this.completeCellEdit.emit({ value: event.value ?? null })
  }

  emitOnCustomEdit() {
    this.onCustomEdit.emit()
  }
}
